import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  X, Loader2, Package, User, MapPin, Calendar, Mail, Phone, FileText, Receipt, ClipboardList,
  CreditCard, Truck, CheckCircle2, Clock, Ban, RotateCcw, ShoppingBag, ExternalLink, Copy,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
  pending_approval: { l: "À valider",     cls: "bg-amber-100 text-amber-800",    Icon: Clock },
  paid:       { l: "Payée",          cls: "bg-emerald-100 text-emerald-800", Icon: CreditCard },
  preparing:  { l: "En préparation", cls: "bg-amber-100 text-amber-800",    Icon: Clock },
  ready:      { l: "Prête",          cls: "bg-sky-100 text-sky-800",        Icon: Package },
  shipped:    { l: "Expédiée",       cls: "bg-indigo-100 text-indigo-800",  Icon: Truck },
  collected:  { l: "Retirée",        cls: "bg-slate-200 text-slate-800",    Icon: CheckCircle2 },
  completed:  { l: "Terminée",       cls: "bg-slate-200 text-slate-800",    Icon: CheckCircle2 },
  cancelled:  { l: "Annulée",        cls: "bg-red-100 text-red-700",        Icon: Ban },
  refunded:   { l: "Remboursée",     cls: "bg-red-100 text-red-700",        Icon: RotateCcw },
};

const fmt = (cents, cur = "EUR") => `${((cents || 0) / 100).toFixed(2)} ${cur.toUpperCase()}`;
const fmtDate = (iso) => iso ? new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

export default function OrderDetailModal({ orderId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!orderId) return;
    setLoading(true);
    api.get(`/admin/orders/${orderId}`)
      .then(r => setData(r.data))
      .catch(e => toast.error("Erreur de chargement : " + (e?.response?.data?.detail || e.message)))
      .finally(() => setLoading(false));
  }, [orderId]);

  if (!orderId) return null;

  const order = data?.order;
  const customer = data?.customer;
  const preparer = data?.preparer;
  const pickup = data?.pickup_location;
  const invoice = data?.invoice;
  const vat = data?.vat_breakdown;
  const history = data?.status_history || [];
  const st = order ? (STATUS_META[order.status] || STATUS_META.paid) : STATUS_META.paid;
  const cur = order?.currency || "EUR";

  const copyId = () => {
    navigator.clipboard?.writeText(order?.id || "");
    toast.success("Identifiant copié");
  };

  const downloadInvoice = async () => {
    try {
      await api.post(`/admin/orders/${order.id}/invoice?doc_type=invoice`);
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/orders/${order.id}/invoice/download?doc_type=invoice`;
      const token = localStorage.getItem("kap_token") || "";
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error("Erreur téléchargement");
      const blob = await resp.blob();
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, "_blank");
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch (e) { toast.error(String(e.message || e)); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-3" onClick={onClose} data-testid="order-detail-modal">
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[94vh] flex flex-col overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-gradient-to-r from-slate-50 to-white">
          {loading || !order ? (
            <div className="flex items-center gap-2 text-sm text-[hsl(var(--muted-foreground))]">
              <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
            </div>
          ) : (
            <div className="flex items-center gap-3 flex-wrap">
              <ShoppingBag className="w-5 h-5 text-[color:var(--brand-sky)]" />
              <h2 className="font-display text-xl font-bold text-[color:var(--brand-navy)]">Commande {invoice?.number ? <span className="font-mono text-[color:var(--brand-sky)]">#{invoice.number}</span> : <span className="font-mono">#{order.id.slice(0, 8).toUpperCase()}</span>}</h2>
              <Badge className={`${st.cls} border-0 text-[10px] font-bold uppercase tracking-wider inline-flex items-center gap-1`}>
                <st.Icon className="w-3 h-3" /> {st.l}
              </Badge>
              <button onClick={copyId} className="p-1 text-slate-400 hover:text-slate-700" title="Copier l'ID" data-testid="order-copy-id">
                <Copy className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-slate-100" data-testid="order-detail-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        {loading || !order ? (
          <div className="flex-1 flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[color:var(--brand-sky)]" /></div>
        ) : (
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
            {/* Ligne de meta */}
            <div className="grid md:grid-cols-4 gap-3">
              <InfoStat label="Passée le" value={fmtDate(order.created_at)} />
              <InfoStat label="Total TTC" value={fmt(order.total_cents, cur)} accent />
              <InfoStat label="Articles" value={(order.line_items || []).reduce((s, l) => s + (l.qty || 0), 0)} />
              <InfoStat label="Mode" value={order.fulfillment_type === "pickup" ? "Click & Collect" : order.fulfillment_type === "booking" ? "Réservation" : "Livraison"} />
            </div>

            {/* Grille 2 colonnes : Client + Expédition/Retrait */}
            <div className="grid md:grid-cols-2 gap-4">
              {/* Client */}
              <div className="rounded-xl border p-4 space-y-2" data-testid="order-detail-customer">
                <div className="text-[10px] uppercase tracking-wider font-bold text-[color:var(--brand-sky)] flex items-center gap-1"><User className="w-3 h-3" /> Client</div>
                <div className="text-sm font-bold text-[color:var(--brand-navy)]">{order.customer_name || "Invité"}</div>
                <div className="space-y-0.5 text-xs text-[hsl(var(--muted-foreground))]">
                  {order.customer_email && <div className="flex items-center gap-1.5"><Mail className="w-3 h-3" /> <a href={`mailto:${order.customer_email}`} className="hover:text-[color:var(--brand-sky)] underline">{order.customer_email}</a></div>}
                  {customer?.phone && <div className="flex items-center gap-1.5"><Phone className="w-3 h-3" /> <a href={`tel:${customer.phone}`} className="hover:text-[color:var(--brand-sky)]">{customer.phone}</a></div>}
                </div>
                {customer ? (
                  <div className="mt-2 pt-2 border-t grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <div className="text-[10px] uppercase tracking-wider font-bold text-[hsl(var(--muted-foreground))]">Commandes</div>
                      <div className="font-bold text-[color:var(--brand-navy)] tabular-nums">{customer.orders_count}</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider font-bold text-[hsl(var(--muted-foreground))]">CA généré</div>
                      <div className="font-bold text-[color:var(--brand-navy)] tabular-nums">{fmt(customer.ltv_cents, cur)}</div>
                    </div>
                  </div>
                ) : (
                  <div className="mt-2 text-[11px] italic text-[hsl(var(--muted-foreground))]">Commande invité — pas de compte client associé.</div>
                )}
                {customer && customer.addresses?.length > 0 && (
                  <div className="mt-2 pt-2 border-t">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-[hsl(var(--muted-foreground))] mb-1">Adresse par défaut</div>
                    {(() => {
                      const def = customer.addresses.find(a => a.is_default_shipping) || customer.addresses[0];
                      if (!def) return null;
                      return (
                        <div className="text-xs text-slate-700 leading-relaxed">
                          {def.street}<br />{def.postal_code} {def.city}{def.country ? ", " + def.country : ""}
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>

              {/* Expédition / Retrait / Préparateur */}
              <div className="rounded-xl border p-4 space-y-2" data-testid="order-detail-fulfillment">
                <div className="text-[10px] uppercase tracking-wider font-bold text-[color:var(--brand-sky)] flex items-center gap-1">
                  {order.fulfillment_type === "pickup" ? <MapPin className="w-3 h-3" /> : <Truck className="w-3 h-3" />}
                  {order.fulfillment_type === "pickup" ? "Retrait en point" : order.fulfillment_type === "booking" ? "Réservation" : "Livraison"}
                </div>
                {order.fulfillment_type === "pickup" && pickup && (
                  <div className="text-sm">
                    <div className="font-bold text-[color:var(--brand-navy)]">{pickup.name}</div>
                    <div className="text-xs text-slate-600 mt-1">{pickup.address}<br />{pickup.postal_code} {pickup.city}</div>
                  </div>
                )}
                {order.fulfillment_type === "pickup" && order.pickup_slot_at && (
                  <div className="text-xs flex items-start gap-1.5 mt-2">
                    <Calendar className="w-3 h-3 mt-0.5 shrink-0 text-[color:var(--brand-sky)]" />
                    <div>
                      <div className="text-[10px] uppercase tracking-wider font-bold text-[hsl(var(--muted-foreground))]">Créneau</div>
                      <div className="text-slate-700 font-semibold">{fmtDate(order.pickup_slot_at)}</div>
                    </div>
                  </div>
                )}
                {order.fulfillment_type !== "pickup" && order.shipping_address && (
                  <div className="text-sm">
                    <div className="text-xs text-slate-600 mt-1">
                      {order.shipping_address.street}<br />
                      {order.shipping_address.postal_code} {order.shipping_address.city}
                      {order.shipping_address.country ? `, ${order.shipping_address.country}` : ""}
                    </div>
                  </div>
                )}
                {preparer && (
                  <div className="mt-2 pt-2 border-t flex items-center gap-2">
                    {preparer.avatar_url ? (
                      <img src={preparer.avatar_url} alt="" className="w-8 h-8 rounded-full object-cover" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center"><User className="w-4 h-4 text-slate-500" /></div>
                    )}
                    <div className="text-xs">
                      <div className="text-[10px] uppercase tracking-wider font-bold text-[hsl(var(--muted-foreground))]">Préparateur assigné</div>
                      <div className="font-semibold text-[color:var(--brand-navy)]">{preparer.first_name} {preparer.last_name}</div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Produits */}
            <div className="rounded-xl border overflow-hidden" data-testid="order-detail-items">
              <div className="px-4 py-2.5 bg-slate-50 text-[10px] uppercase tracking-wider font-bold text-[color:var(--brand-sky)] flex items-center gap-1">
                <Package className="w-3 h-3" /> Articles commandés
              </div>
              <table className="w-full text-sm">
                <thead className="text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))] bg-slate-50/70">
                  <tr>
                    <th className="text-left px-4 py-2 font-bold">Produit</th>
                    <th className="text-center px-2 py-2 font-bold w-16">Qté</th>
                    <th className="text-right px-2 py-2 font-bold">P.U.</th>
                    <th className="text-right px-2 py-2 font-bold">TVA</th>
                    <th className="text-right px-4 py-2 font-bold">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {(order.line_items || []).map((li, i) => {
                    const img = li.product?.image;
                    const slug = li.product?.slug;
                    const rate = li.vat_rate ?? li.product?.vat_rate;
                    return (
                      <tr key={i} className="hover:bg-slate-50/60" data-testid={`order-item-${i}`}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            {img ? (
                              <img src={img} alt="" className="w-12 h-12 rounded-lg object-cover border shrink-0" />
                            ) : (
                              <div className="w-12 h-12 rounded-lg bg-slate-100 border flex items-center justify-center shrink-0"><Package className="w-4 h-4 text-slate-400" /></div>
                            )}
                            <div className="min-w-0">
                              <div className="font-semibold text-[color:var(--brand-navy)] truncate">{li.name}</div>
                              <div className="text-[11px] text-[hsl(var(--muted-foreground))] flex items-center gap-2 flex-wrap">
                                {li.product?.sku && <span className="font-mono">SKU {li.product.sku}</span>}
                                {slug && (
                                  <a href={`/shop/${slug}`} target="_blank" rel="noopener noreferrer" className="text-[color:var(--brand-sky)] hover:underline inline-flex items-center gap-0.5">
                                    Voir la fiche <ExternalLink className="w-2.5 h-2.5" />
                                  </a>
                                )}
                                {li.product?.stock_tracking && (
                                  <span className={li.product.stock_qty === 0 ? "text-red-600 font-bold" : li.product.stock_qty < 3 ? "text-amber-600" : "text-emerald-600"}>
                                    Stock actuel : {li.product.stock_qty}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="text-center px-2 py-3 font-bold tabular-nums">{li.qty}</td>
                        <td className="text-right px-2 py-3 tabular-nums">{fmt(li.price_cents, cur)}</td>
                        <td className="text-right px-2 py-3 text-xs text-slate-600 tabular-nums">{rate !== undefined && rate !== null && rate >= 0 ? `${rate}%` : "—"}</td>
                        <td className="text-right px-4 py-3 font-bold text-[color:var(--brand-navy)] tabular-nums">{fmt((li.price_cents || 0) * (li.qty || 0), cur)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Totaux TVA */}
            {vat && (
              <div className="flex justify-end">
                <div className="rounded-xl border p-4 min-w-[280px] bg-slate-50/50 space-y-1.5" data-testid="order-detail-totals">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Sous-total HT</span>
                    <span className="tabular-nums font-semibold">{fmt(vat.total_ht_cents, cur)}</span>
                  </div>
                  {(vat.by_rate || []).map((br, i) => (
                    <div key={i} className="flex justify-between text-sm">
                      <span className="text-slate-600">TVA {br.rate}%</span>
                      <span className="tabular-nums">{fmt(br.tva_cents, cur)}</span>
                    </div>
                  ))}
                  {vat.vat_applicable === false && (
                    <div className="text-[11px] italic text-amber-700 pt-1">TVA non applicable, art. 293 B du CGI</div>
                  )}
                  <div className="flex justify-between text-base pt-2 border-t mt-1">
                    <span className="font-bold text-[color:var(--brand-navy)]">{vat.vat_applicable === false ? "NET À PAYER" : "TOTAL TTC"}</span>
                    <span className="tabular-nums font-bold text-[color:var(--brand-navy)]">{fmt(vat.total_ttc_cents, cur)}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Timeline statuts */}
            {history.length > 0 && (
              <div className="rounded-xl border p-4" data-testid="order-detail-timeline">
                <div className="text-[10px] uppercase tracking-wider font-bold text-[color:var(--brand-sky)] flex items-center gap-1 mb-3"><Calendar className="w-3 h-3" /> Historique des statuts</div>
                <ol className="space-y-2.5">
                  {history.map((h, i) => {
                    const m = STATUS_META[h.status] || { l: h.status, cls: "bg-slate-100 text-slate-700", Icon: CheckCircle2 };
                    return (
                      <li key={i} className="flex items-start gap-3">
                        <div className={`w-7 h-7 rounded-full ${m.cls} flex items-center justify-center shrink-0`}>
                          <m.Icon className="w-3.5 h-3.5" />
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-[color:var(--brand-navy)]">{m.l}</div>
                          <div className="text-[11px] text-[hsl(var(--muted-foreground))]">{fmtDate(h.at)}{h.from ? ` · depuis ${STATUS_META[h.from]?.l || h.from}` : ""}</div>
                        </div>
                      </li>
                    );
                  })}
                </ol>
              </div>
            )}

            {/* Actions documents */}
            {order.status === "pending_approval" && (
              <div className="p-4 rounded-xl border-2 border-amber-300 bg-amber-50 flex items-center justify-between flex-wrap gap-3" data-testid="approval-actions">
                <div className="text-sm">
                  <div className="font-bold text-amber-900">Commande en attente d'approbation</div>
                  <div className="text-xs text-amber-800">Approuvez pour envoyer en préparation (déclenche l'email client), ou rejetez pour annuler.</div>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={async () => {
                    if (!window.confirm("Rejeter cette commande ? (elle sera annulée)")) return;
                    try {
                      await api.post(`/admin/orders/${order.id}/reject`);
                      toast.success("Commande rejetée");
                      onClose();
                    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
                  }} className="text-red-600 border-red-300 hover:bg-red-50" data-testid="reject-order-btn">
                    <Ban className="w-3.5 h-3.5 mr-1" /> Rejeter
                  </Button>
                  <Button size="sm" onClick={async () => {
                    try {
                      await api.post(`/admin/orders/${order.id}/approve`);
                      toast.success("Commande approuvée, en préparation");
                      onClose();
                    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
                  }} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="approve-order-btn">
                    <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approuver et préparer
                  </Button>
                </div>
              </div>
            )}
            <div className="flex items-center gap-2 flex-wrap pt-2 border-t">
              <Button variant="outline" size="sm" onClick={downloadInvoice} className="text-xs" data-testid="detail-download-invoice">
                <Receipt className="w-3.5 h-3.5 mr-1" /> {invoice?.number ? `Télécharger ${invoice.number}` : "Générer & télécharger la facture"}
              </Button>
              <Button variant="outline" size="sm" onClick={() => downloadDoc(order.id, "delivery")} className="text-xs" data-testid="detail-download-bl">
                <ClipboardList className="w-3.5 h-3.5 mr-1" /> Bon de livraison
              </Button>
              <Button variant="outline" size="sm" onClick={() => downloadDoc(order.id, "purchase")} className="text-xs" data-testid="detail-download-bc">
                <FileText className="w-3.5 h-3.5 mr-1" /> Bon de commande
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

async function downloadDoc(oid, docType) {
  try {
    await api.post(`/admin/orders/${oid}/invoice?doc_type=${docType}`);
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/orders/${oid}/invoice/download?doc_type=${docType}`;
    const token = localStorage.getItem("kap_token") || "";
    const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!resp.ok) throw new Error("Erreur téléchargement");
    const blob = await resp.blob();
    const blobUrl = URL.createObjectURL(blob);
    window.open(blobUrl, "_blank");
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
  } catch (e) { toast.error(String(e.message || e)); }
}

function InfoStat({ label, value, accent = false }) {
  return (
    <div className={`rounded-xl border p-3 ${accent ? "bg-[color:var(--brand-navy)] text-white border-transparent" : ""}`}>
      <div className={`text-[10px] uppercase tracking-wider font-bold ${accent ? "text-white/70" : "text-[hsl(var(--muted-foreground))]"}`}>{label}</div>
      <div className={`mt-1 font-display font-bold text-lg ${accent ? "text-white" : "text-[color:var(--brand-navy)]"}`}>{value}</div>
    </div>
  );
}
